import moment from "moment";

import type {
    IMessagePlaceholder,
    IMessageUser
} from "../../../resources-vue/vue-interfaces/messaging-service/i-message";
import { PlaceholdersListEnum } from "../../../enums/PlaceholdersListEnum";
import { useUserStore } from "../../../pages-vue/MessageCenter/store/useUsersStore";

export const formatUserInitials = (value: IMessageUser | string) => {
    if (!value) return null;

    let firstName = "";
    let lastName = "";

    if (typeof value === "string") {
        const parts = value.split(" ");
        firstName = parts[0]?.trim() ?? "";
        lastName = parts[1]?.trim() ?? "";
    } else {
        firstName = value.firstName?.trim() ?? "";
        lastName = value.lastName?.trim() ?? "";
    }

    const firstInitial = firstName.substring(0, 1);
    const lastInitial = lastName.substring(0, 1);

    return `${firstInitial}${lastInitial}`;
};

export const formatDisplayUsers = (value: IMessageUser[], limit: number = 30) => {
    if (value) {
        if (value.length > 1) {
            return multipleUsersLabel(value, limit);
        } else {
            return value[0]?.firstName + (value[0]?.lastName ? ` ${value[0].lastName}` : "");
        }
    } else {
        return null;
    }
};

export const formatConversationTitle = (loginIds: string[], limit: number = 30) => {
    if (loginIds) {
        const userStore = useUserStore();

        if (loginIds.length > 1) {
            let userListIndexCount: number = 0;
            let userFirstNames: string[] = [];
            for (let id of loginIds) {
                const userDetails = userStore.getUser(id);
                // Add 2 for space and commas
                userListIndexCount += userDetails.firstName.length + 2;
                // Assuming max characters to be 30
                if (userListIndexCount > limit) {
                    break;
                }
                userFirstNames.push(userDetails.firstName);
            }
            // Other users count
            let additionalUsers: number = loginIds.length - userFirstNames.length;
            if (additionalUsers > 0) {
                return userFirstNames.join(", ") + ` + ${additionalUsers}`;
            } else {
                return userFirstNames.join(", ");
            }
        } else {
            const userDetails = userStore.getUser(loginIds[0]);
            return userDetails?.firstName + (userDetails?.lastName ? ` ${userDetails.lastName}` : "");
        }
    } else {
        return null;
    }
};

export const formatMessageTime = (value: Date | string) => {
    if (value) {
        let today = moment();
        let messageTime = moment.utc(value).local();
        let numberOfDays = messageTime.diff(today, "days");
        let numberOfYears = messageTime.diff(today, "year");
        if (numberOfDays <= 0 || numberOfYears <= 0) {
            if (numberOfDays === 0 || numberOfYears === 0) {
                return messageTime.format("hh:mm A");
            } else {
                return messageTime.format("MM/DD/YY hh:mm A");
            }
            // commenting as the UI/UX has not finalized on the format yet
            // if (numberOfDays === 0) {
            //     return messageTime.format("hh:mm A");
            // } else if (numberOfYears === 0) {
            //     return messageTime.format("MM/DD hh:mm A");
            // } else {
            //     return messageTime.format("MM/DD/YY hh:mm A");
            // }
        } else {
            // Server time error
            return "";
        }
    } else {
        return null;
    }
};

export function formatDateToLocalBasedOnDayMode(dateString: string, isSideNav: boolean = false): string {
    const date = new Date(dateString);
    const now = new Date();

    const timeZoneOffset = date.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(date.getTime() - timeZoneOffset);

    // Check if the date is today or not
    const isToday = localDate.toDateString() === now.toDateString();

    // Check if the date is yesterday
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = localDate.toDateString() === yesterday.toDateString();

    // Format the time
    const hours = localDate.getHours();
    const minutes = localDate.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, "0");

    if (isToday) {
        if (isSideNav) {
            return `${formattedHours}:${formattedMinutes} ${ampm}`;
        }
        return `Today`;
    } else {
        const month = localDate.getMonth() + 1;
        const day = localDate.getDate();
        return `${month}/${day} `;
    }
}

export function formatDateTimeToLocal(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();

    const timeZoneOffset = date.getTimezoneOffset() * 60 * 1000;
    const localDate = new Date(date.getTime() - timeZoneOffset);

    // Check if the date is today or not
    const isToday = localDate.toDateString() === now.toDateString();

    if (isToday) {
        return `Today ${moment(localDate).format("hh:mm A")}`;
    } else {
        return moment(localDate).format("MMM DD, YYYY hh:mm A");
    }
}

export const formatDisplayMessage = (value: string) => {
    if (value) {
        let div = document.createElement("div");
        div.innerHTML = value;
        let text = div.textContent;
        return text.substring(0, 40);
    } else {
        return null;
    }
};

const multipleUsersLabel = (value: IMessageUser[], limit: number) => {
    let userListIndexCount: number = 0;
    let userFirstNames: string[] = [];
    for (let user of value) {
        // Add 2 for space and commas
        userListIndexCount += user.firstName.length + 2;
        // Assuming max characters to be 30
        if (userListIndexCount > limit) {
            break;
        }
        userFirstNames.push(user.firstName);
    }
    // Other users count
    let additionalUsers: number = value.length - userFirstNames.length;
    if (additionalUsers > 0) {
        return userFirstNames.join(", ") + ` + ${additionalUsers}`;
    } else {
        return userFirstNames.join(", ");
    }
};

export const truncateString = (str: string, maxLength: number) => {
    return str.length > maxLength ? `${str.slice(0, maxLength - 3)}...` : str;
};

export const formatTaggedUserMessage = (
    text: string,
    placeholdersList: IMessagePlaceholder[],
    currentUserLoginId: string
) => {
    // If message has tagged users, replace placeholders with usernames
    let showTagSignForMsg: boolean = false;
    if (placeholdersList?.length > 0 && text != null) {
        let formattedText: string = text;
        placeholdersList.forEach((user: any) => {
            if (user.type === PlaceholdersListEnum.TaggedUser) {
                const highlightTaggedUser = user.id === currentUserLoginId;
                if (!showTagSignForMsg) {
                    showTagSignForMsg = highlightTaggedUser;
                }
                formattedText = formattedText.replace(
                    "%{TAGGED_USER}%",
                    `<span class='${highlightTaggedUser ? "text-danger font-weight-bold" : "fw-semibold"}'>${
                        user.name
                    }</span>`
                );
            }
        });
        return { text: formattedText, showTagSignForMsg };
    }
    return { text, showTagSignForMsg };
};

export const sortPlaceholdersList = (placeHoldersList: IMessagePlaceholder[]) => {
    let taggedUsers: IMessagePlaceholder[] = [];
    let sharedProfiles: IMessagePlaceholder[] = [];
    if (placeHoldersList?.length > 0) {
        placeHoldersList.forEach((item: IMessagePlaceholder) => {
            if (item.type === PlaceholdersListEnum.TaggedUser) {
                taggedUsers.push(item);
            } else if ([PlaceholdersListEnum.ShareUser, PlaceholdersListEnum.SharePatient].includes(item.type)) {
                sharedProfiles.push(item);
            }
        });
    }
    return { taggedUsers, sharedProfiles };
};
